import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import CompteSelect from "@/components/CompteSelect";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, FileText, User, MapPin, Calendar, CreditCard, Car,
  Loader2, BookOpen, Receipt, Plus, Save, Trash2, Undo2, Pencil,
  Percent, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import VehiculeCard from "@/components/VehiculeCard";
import FactureSendActions from "@/components/facturation/FactureSendActions";
import LignesEditor from "@/components/LignesEditor";
import DocumentHeader from "@/components/DocumentHeader";

const STATUT_LABELS = { payee: "Payée", en_suspens: "En suspens", partiel: "Partiel", annule: "Annulée" };

// iter300 — un mouvement sur facture : encaissement réel, escompte accordé ou perte sur client
const NATURES = {
  encaissement: { label: "Encaissement", compte: null, couleur: "text-emerald-700", signe: "+" },
  escompte: { label: "Escompte accordé", compte: "3800", couleur: "text-blue-700", signe: "−" },
  perte: { label: "Perte sur client", compte: "6960", couleur: "text-red-700", signe: "−" },
};
const STATUT_COLORS = {
  payee: "bg-emerald-50 text-emerald-700 border-emerald-200",
  en_suspens: "bg-amber-50 text-amber-700 border-amber-200",
  partiel: "bg-blue-50 text-blue-700 border-blue-200",
  annule: "bg-gray-100 text-gray-500 border-gray-200",
};

function fmtChf(n) {
  if (n == null) return "—";
  return n.toLocaleString("fr-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtDate(s) {
  if (!s) return "—";
  const [y, m, d] = s.split("-");
  return `${d}.${m}.${y}`;
}

export default function FactureDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [f, setF] = useState(null);
  const [loading, setLoading] = useState(true);
  const [encNature, setEncNature] = useState(null);  // iter300 — encaissement | escompte | perte
  const { user } = useAuth();
  const isAdmin = (user?.role || "").toLowerCase() === "admin";
  const [comptes, setComptes] = useState([]);
  // iter122 — Édition des lignes (avant tout encaissement)
  const [editLignesOpen, setEditLignesOpen] = useState(false);

  const reload = async () => {
    const { data } = await api.get(`/factures/${id}`);
    setF(data);
  };

  const handleDelete = async (force = false) => {
    const lbl = `Facture ${f.numero} · ${f.contact_label} · ${(f.montant_brut || 0).toFixed(2)} CHF`;
    const warning = force
      ? `⚠️ FORCER LA SUPPRESSION de cette facture payée ?\n\n${lbl}\n\nCela supprimera aussi tous les encaissements et écritures comptables liées.\nUn audit log est conservé.\nCette action est IRRÉVERSIBLE.\n\nUtilisez plutôt une contre-écriture si vous voulez conserver la traçabilité Swiss-compliant.`
      : `Supprimer définitivement cette facture ?\n\n${lbl}\n\nLes écritures comptables liées seront aussi supprimées (audit log conservé).\nCette action est IRRÉVERSIBLE.`;
    if (!window.confirm(warning)) return;
    try {
      const { data } = await api.delete(`/factures/${id}${force ? "?force=true" : ""}`);
      toast.success(`Facture supprimée (${data.ecritures_supprimees} écriture(s))`);
      navigate("/facturation");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  const handleReactiver = async () => {
    if (!window.confirm(
      `Annuler l'annulation de cette facture ?\n\n` +
      `Facture ${f.numero} · ${(f.montant_brut || 0).toFixed(2)} CHF\n\n` +
      `L'app va :\n` +
      `  1. Supprimer les contre-écritures d'extourne\n` +
      `  2. Restaurer le statut antérieur de la facture\n` +
      `${f.or_id ? "  3. Re-lier l'OR à cette facture\n" : ""}` +
      `\nL'écriture comptable initiale redevient active. Continuer ?`
    )) return;
    try {
      const { data } = await api.post(`/factures/${id}/reactiver`);
      toast.success(data.message || `Facture ${data.facture_numero} réactivée`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Réactivation impossible", { duration: 6000 });
    }
  };

  const handleAnnuler = async () => {
    const lbl = `Facture ${f.numero} · ${f.contact_label} · ${(f.montant_brut || 0).toFixed(2)} CHF`;
    const motif = window.prompt(
      `Annuler cette facture pour correction ?\n\n${lbl}\n\nL'app va automatiquement :\n  1. Générer les contre-écritures comptables (ventes 3200/3400, TVA, encaissements)\n  2. Marquer la facture en statut "Annulée" (rien n'est supprimé — conforme CO 957)\n${f.or_id ? "  3. Repasser l'OR en statut 'pret' pour que vous puissiez le modifier puis ré-émettre la facture\n" : ""}\nMotif de l'annulation :`,
      "Oubli de lignes (pièces, main d'œuvre)"
    );
    if (motif === null) return;
    try {
      const { data } = await api.post(`/factures/${id}/annuler`, { motif: motif || "Correction" });
      toast.success(data.message || `Facture ${data.facture_numero} annulée`);
      if (data.or_delie && data.or_id) {
        // Redirection automatique vers l'OR pour modification
        setTimeout(() => navigate(`/or/${data.or_id}`), 1200);
      } else {
        await reload();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Annulation impossible");
    }
  };

  const handleDeleteEncaissement = async (enc) => {
    const lbl = `${fmtDate(enc.date)} · ${enc.compte_libelle || enc.compte_numero} · ${(enc.montant || 0).toFixed(2)} CHF`;
    if (!window.confirm(
      `Supprimer cet encaissement ?\n\n${lbl}\n\nL'écriture comptable liée sera également supprimée et le statut de la facture sera recalculé.\nVous pourrez ensuite enregistrer un nouvel encaissement avec le bon compte.`
    )) return;
    try {
      const { data } = await api.delete(`/factures/${id}/encaissements/${enc.id}`);
      toast.success(`Encaissement supprimé · facture ${data.nouveau_statut === "payee" ? "payée" : data.nouveau_statut} · reste ${data.reste_a_payer.toFixed(2)} CHF`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Suppression impossible");
    }
  };

  // Rôle utilisateur (pour afficher bouton Supprimer admin uniquement)
  const userRole = (() => {
    try { return JSON.parse(localStorage.getItem("cm_user") || "{}").role; }
    catch { return null; }
  })();

  useEffect(() => {
    (async () => {
      try {
        const [{ data: facture }, { data: pc }] = await Promise.all([
          api.get(`/factures/${id}`),
          api.get("/journal/plan-comptes"),
        ]);
        setF(facture);
        // iter202 — Filtre comptes d'encaissement courants : Caisse (1000),
        // Lixt (1001), Banques et wallets (102x). Le regex précédent excluait
        // 1001 Lixt → les paiements retenus par Lixt étaient impossibles à
        // saisir depuis ce formulaire, obligeant à passer par le journal.
        setComptes(pc.filter((c) => /^10(0[01]|2)/.test(c.numero) && c.actif));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading || !f) return <div className="p-8 text-center text-gray-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;

  const c = f.client || {};
  const cname = c.raison_sociale || `${c.prenom || ""} ${c.nom || ""}`.trim() || f.contact_label;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="facture-detail">
      <button onClick={() => navigate("/facturation")} className="text-sm text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 mb-3" data-testid="facture-back">
        <ArrowLeft className="w-4 h-4" /> Retour aux factures
      </button>

      <DocumentHeader
        icon={FileText}
        numero={f.numero}
        testidNumero="facture-numero"
        badges={
          <>
            <Badge variant="outline" className={STATUT_COLORS[f.statut]}>{STATUT_LABELS[f.statut]}</Badge>
            {f.origine?.startsWith("import_") && (
              <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">Importée Bexio</Badge>
            )}
          </>
        }
        totalTtc={f.montant_brut}
        monnaie={f.monnaie}
        resteAPayer={f.reste_a_payer}
        testidRoot="facture-header"
      >
        <div className="text-sm text-gray-600 flex items-center gap-3 flex-wrap mt-1">
          <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-gray-400" /> Émise le {fmtDate(f.date_emission)}</span>
          <span className="inline-flex items-center gap-1 text-gray-500">· Échéance {fmtDate(f.date_echeance)}</span>
        </div>
        {f.reference_libre && (
          <div className="text-xs text-gray-500 mt-1 italic">{f.reference_libre}</div>
        )}
      </DocumentHeader>

      {/* iter92 — Ré-envoi de la facture (Mail / Lixt une-fois / Courrier) si non soldée */}
      <FactureSendActions facture={f} onRefresh={reload} />

      {/* iter122 — Détail des lignes + édition si pas d'encaissement */}
      <FactureLignesSection facture={f} onEdit={() => setEditLignesOpen(true)} userRole={userRole} />

      <EditFactureLignesDialog
        open={editLignesOpen}
        onClose={() => setEditLignesOpen(false)}
        facture={f}
        onSaved={async () => { await reload(); setEditLignesOpen(false); }}
      />

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Client */}
        <Card className="p-4 border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Client</div>
          <button
            onClick={() => c.id && navigate(`/clients/${c.id}`)}
            disabled={!c.id}
            className="text-left w-full disabled:cursor-default"
          >
            <div className="font-semibold text-gray-900 flex items-center gap-1.5">
              <User className="w-4 h-4 text-gray-400" /> {cname}
            </div>
            {c.adresse && (
              <div className="text-xs text-gray-600 mt-1 flex items-start gap-1.5">
                <MapPin className="w-3 h-3 text-gray-400 mt-0.5" />
                <span>{c.adresse}<br/>{c.npa} {c.localite}</span>
              </div>
            )}
            {c.tel_principal && <div className="text-xs text-gray-500 mt-1">📞 {c.tel_principal}</div>}
            {c.email && <div className="text-xs text-gray-500">✉ {c.email}</div>}
          </button>
          {f.vehicule_label && (
            <div className="mt-3 pt-3 border-t border-gray-100" data-testid="facture-vehicule-display">
              <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-1">Véhicule</div>
              <div className="text-sm text-gray-800 flex items-center gap-1.5">
                <Car className="w-4 h-4 text-gray-400" /> {f.vehicule_label}
              </div>
              {/* iter85-invoice — Affichage explicite km au dépôt + 1ère mise en circulation */}
              <div className="mt-2 grid grid-cols-2 gap-x-2 gap-y-1 text-xs" data-testid="facture-vehicule-km-mec">
                <div className="text-gray-500">Kilométrage au dépôt :</div>
                <div className="font-semibold text-gray-800" data-testid="facture-km-depose">
                  {f.km_depose != null && f.km_depose !== ""
                    ? `${Number(f.km_depose).toLocaleString("fr-CH").replace(/\u202f|\u00a0|,/g, "'")} km`
                    : "—"}
                </div>
                <div className="text-gray-500">1ère mise en circulation :</div>
                <div className="font-semibold text-gray-800" data-testid="facture-premiere-mec">
                  {fmtDate(f.vehicule_detail?.premiere_mec)}
                </div>
              </div>
              {/* iter70 — Édition inline plaque/châssis/marque/modèle/1ère MEC */}
              {f.vehicule_detail && (
                <VehiculeCard
                  vehicule={f.vehicule_detail}
                  onUpdate={() => reload()}
                />
              )}
            </div>
          )}
        </Card>

        {/* Détails financiers */}
        <Card className="p-4 border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Montants</div>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">HT</span><span className="font-mono">{fmtChf(f.montant_net)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">TVA 8.10 %</span><span className="font-mono">{fmtChf(f.tva_810)}</span></div>
            {f.tva_0 > 0 && <div className="flex justify-between"><span className="text-gray-600">TVA 0 %</span><span className="font-mono">{fmtChf(f.tva_0)}</span></div>}
            <div className="flex justify-between border-t pt-1.5 font-semibold"><span>TTC</span><span className="font-mono">{fmtChf(f.montant_brut)}</span></div>
          </div>
        </Card>

        {/* Métadonnées */}
        <Card className="p-4 border-gray-200">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold mb-2">Détails</div>
          <div className="space-y-1.5 text-xs text-gray-700">
            <div><span className="text-gray-500">Compte bancaire :</span> {f.compte_bancaire || "—"}</div>
            <div><span className="text-gray-500">Mode :</span> {f.mode_paiement}</div>
            <div><span className="text-gray-500">Interlocuteur :</span> {f.interlocuteur || "—"}</div>
            {f.document_precedent && <div><span className="text-gray-500">Doc. précédent :</span> {f.document_precedent}</div>}
            {f.ref_qr && <div className="break-all"><span className="text-gray-500">Réf. QR :</span> <code className="text-[10px]">{f.ref_qr}</code></div>}
          </div>
        </Card>
      </div>

      {/* Encaissements */}
      <Card className="p-5 border-gray-200 mt-4" data-testid="facture-encaissements">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-[#52a675]" /> Encaissements ({(f.encaissements || []).length})
          </h3>
          {f.statut !== "payee" && f.statut !== "annule" && (
            <div className="flex items-center gap-2 flex-wrap">
              <Button size="sm" onClick={() => setEncNature("encaissement")} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="facture-add-encaissement-btn">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Enregistrer un encaissement
              </Button>
              <Button size="sm" variant="outline" onClick={() => setEncNature("escompte")} data-testid="facture-add-escompte-btn">
                <Percent className="w-3.5 h-3.5 mr-1.5" /> Escompte
              </Button>
              {isAdmin && (
                <Button size="sm" variant="outline" onClick={() => setEncNature("perte")} className="text-red-700 hover:text-red-800" data-testid="facture-add-perte-btn">
                  <TrendingDown className="w-3.5 h-3.5 mr-1.5" /> Perte sur client
                </Button>
              )}
            </div>
          )}
        </div>
        {(f.encaissements || []).length === 0 ? (
          <div className="text-sm text-gray-400 py-4 text-center">Aucun encaissement enregistré</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Compte</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                {f.statut !== "annule" && <th className="px-3 py-2 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {f.encaissements.map((e, i) => (
                <tr key={e.id || i} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-700">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2 text-gray-700">
                    {(NATURES[e.nature || "encaissement"] || NATURES.encaissement).label !== "Encaissement" && (
                      <span className="mr-2 text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 uppercase tracking-wider">
                        {(NATURES[e.nature] || NATURES.encaissement).label}
                      </span>
                    )}
                    {e.compte_libelle || `${e.compte_numero || ""}`}
                    {e.mode && <span className="ml-2 text-[10px] uppercase tracking-wider text-gray-400">({e.mode})</span>}
                    {e.tva_corrigee > 0 && (
                      <span className="ml-2 text-[10px] text-gray-400">TVA corrigée {fmtChf(e.tva_corrigee)}</span>
                    )}
                  </td>
                  <td className={`px-3 py-2 text-right font-mono font-semibold ${(NATURES[e.nature] || NATURES.encaissement).couleur}`}>
                    {(NATURES[e.nature] || NATURES.encaissement).signe} {fmtChf(e.montant)}
                  </td>
                  {f.statut !== "annule" && (
                    <td className="px-2 py-1 text-right">
                      {e.id && (
                        <button
                          type="button"
                          onClick={() => handleDeleteEncaissement(e)}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded p-1"
                          title="Supprimer cet encaissement (et son écriture comptable)"
                          data-testid={`facture-delete-encaissement-${e.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {["escompte", "perte"].map((nat) => {
                const somme = (f.encaissements || [])
                  .filter((e) => e.nature === nat)
                  .reduce((s, e) => s + (e.montant || 0), 0);
                if (!somme) return null;
                return (
                  <tr key={nat} className="border-t border-gray-100">
                    <td colSpan={f.statut !== "annule" ? 3 : 2} className="px-3 py-2 text-gray-600">
                      Dont {NATURES[nat].label.toLowerCase()}
                    </td>
                    <td className={`px-3 py-2 text-right font-mono ${NATURES[nat].couleur}`} data-testid={`facture-total-${nat}`}>
                      {fmtChf(somme)}
                    </td>
                  </tr>
                );
              })}
              <tr className="border-t-2 border-gray-200 bg-emerald-50/50">
                <td colSpan={f.statut !== "annule" ? 3 : 2} className="px-3 py-2 font-semibold text-gray-900">Total soldé</td>
                <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">{fmtChf(f.total_encaisse)}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Card>

      {/* Dialog encaissement */}
      <EncaissementDialog
        nature={encNature}
        onClose={() => setEncNature(null)}
        facture={f}
        comptes={comptes}
        onSaved={async () => { await reload(); setEncNature(null); }}
      />

      {/* Écritures liées */}
      {f.ecritures_liees && f.ecritures_liees.length > 0 && (
        <Card className="p-5 border-gray-200 mt-4" data-testid="facture-ecritures">
          <h3 className="font-heading font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#52a675]" /> Écritures comptables ({f.ecritures_liees.length})
          </h3>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Débit</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Crédit</th>
                <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase">TVA</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Montant</th>
                <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500 uppercase w-12"></th>
              </tr>
            </thead>
            <tbody>
              {f.ecritures_liees.map((e) => (
                <tr key={e.id} className="border-t border-gray-100">
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{fmtDate(e.date)}</td>
                  <td className="px-3 py-2 text-gray-800"><span className="font-mono text-xs text-gray-500">{e.debit_compte_numero}</span> {e.debit_compte_libelle}</td>
                  <td className="px-3 py-2 text-gray-800"><span className="font-mono text-xs text-gray-500">{e.credit_compte_numero}</span> {e.credit_compte_libelle}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{e.tva_code || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtChf(e.montant)}</td>
                  <td className="px-3 py-2 text-right">
                    <EditEcritureFactureBtn ecriture={e} factureStatut={f.statut} onSaved={reload} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* PDF QR-bill */}
      <Card className="p-4 mt-4 bg-[#f0f9f4] border-[#52a675]/30">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-700">
            {f.or_id
              ? "Cette facture est liée à un OR de l'atelier — utilisez le PDF depuis la fiche OR (détails véhicule)."
              : "Génère le PDF facture avec bulletin QR-bill suisse intégré (norme v2.x, bord inférieur)."}
          </div>
          <div className="flex gap-2">
            {f.or_id && (
              <Button onClick={() => navigate(`/atelier/or/${f.or_id}`)} variant="outline" size="sm">
                <Receipt className="w-3.5 h-3.5 mr-1.5" /> Voir l&apos;OR
              </Button>
            )}
            <Button
              onClick={() => {
                const token = localStorage.getItem("cm_token");
                const url = f.or_id
                  ? `${process.env.REACT_APP_BACKEND_URL}/api/or/${f.or_id}/facture/pdf`
                  : `${process.env.REACT_APP_BACKEND_URL}/api/factures/${f.id}/pdf`;
                fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.blob())
                  .then((blob) => {
                    const u = URL.createObjectURL(blob);
                    window.open(u, "_blank", "noopener");
                    setTimeout(() => URL.revokeObjectURL(u), 60000);
                  })
                  .catch(() => toast.error("Erreur PDF"));
              }}
              className="bg-[#52a675] hover:bg-[#3d8a5c]"
              size="sm"
              data-testid="facture-pdf-btn"
            >
              <FileText className="w-3.5 h-3.5 mr-1.5" />
              {f.invoice_method === "caisse" ? "Aperçu PDF (comptoir)" : "Aperçu PDF QR-bill"}
            </Button>
            {/* iter176 — Bouton dédié : version 30j avec QR-bill pour une facture caisse */}
            {f.invoice_method === "caisse" && (
              <Button
                onClick={() => {
                  const token = localStorage.getItem("cm_token");
                  const url = f.or_id
                    ? `${process.env.REACT_APP_BACKEND_URL}/api/or/${f.or_id}/facture/pdf?with_qr=1`
                    : `${process.env.REACT_APP_BACKEND_URL}/api/factures/${f.id}/pdf?with_qr=1`;
                  fetch(url, { headers: { Authorization: `Bearer ${token}` } })
                    .then((r) => r.blob())
                    .then((blob) => {
                      const u = URL.createObjectURL(blob);
                      window.open(u, "_blank", "noopener");
                      setTimeout(() => URL.revokeObjectURL(u), 60000);
                    })
                    .catch(() => toast.error("Erreur PDF"));
                }}
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-800 hover:bg-amber-50"
                data-testid="facture-pdf-with-qr-btn"
                title="Générer la version envoi 30 jours (avec bulletin QR-bill)"
              >
                <FileText className="w-3.5 h-3.5 mr-1.5" /> Version 30j (QR-bill)
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Zone correction : annuler la facture (extourne comptable) */}
      {f.statut !== "annule" && !f.cloture_exercice_id && f.origine !== "bexio" && ["admin", "comptoir", "compta"].includes(userRole) && (
        <Card className="p-4 mt-4 bg-amber-50 border-amber-200" data-testid="facture-annuler-zone">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-amber-900 max-w-2xl">
              <strong>Erreur ou oubli sur cette facture ?</strong> Annule la facture en générant les contre-écritures
              comptables automatiquement (conforme CO art. 957). {f.or_id
                ? "L'OR sera repassé en 'prêt' pour que vous puissiez le modifier puis ré-émettre la facture."
                : "La facture sera marquée annulée et retirée de la file caisse."}
            </div>
            <Button
              onClick={handleAnnuler}
              variant="outline"
              size="sm"
              className="border-amber-400 text-amber-800 hover:bg-amber-100"
              data-testid="facture-annuler-btn"
            >
              <Undo2 className="w-3.5 h-3.5 mr-1.5" />
              Annuler la facture pour correction
            </Button>
          </div>
        </Card>
      )}

      {/* Bandeau facture déjà annulée */}
      {f.statut === "annule" && (
        <Card className="p-4 mt-4 bg-gray-50 border-gray-300" data-testid="facture-annulee-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="text-sm text-gray-700 max-w-2xl">
              <strong>Facture annulée</strong>
              {f.annule_at && <> le {fmtDate(f.annule_at.slice(0, 10))}</>}
              {f.annule_par_nom && <> par <strong>{f.annule_par_nom}</strong></>}
              {f.annule_motif && <> · motif : <em>{f.annule_motif}</em></>}
              . Les contre-écritures comptables ont été générées (rien n&apos;est effacé du journal — traçabilité CO 957).
            </div>
            {userRole === "admin" && !f.cloture_exercice_id && (
              <Button
                onClick={handleReactiver}
                variant="outline"
                size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                data-testid="facture-reactiver-btn"
              >
                <Undo2 className="w-3.5 h-3.5 mr-1.5" />
                Annuler l&apos;annulation
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Zone admin : suppression facture */}
      {userRole === "admin" && f.origine !== "bexio" && !f.cloture_exercice_id && (
        <Card className="p-4 mt-4 bg-red-50 border-red-200" data-testid="facture-admin-zone">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm text-red-900 max-w-2xl">
              <strong>Zone admin :</strong> supprimer définitivement cette facture (avec ses écritures comptables).
              {f.statut !== "en_suspens" && (
                <span className="block text-xs mt-1 italic">
                  Cette facture est <strong>{f.statut}</strong> avec {(f.encaissements || []).length} encaissement(s).
                  Une suppression forcée annulera aussi tous les encaissements (réversible via audit log).
                </span>
              )}
            </div>
            <Button
              onClick={() => handleDelete(f.statut !== "en_suspens" || (f.encaissements || []).length > 0)}
              variant="outline"
              size="sm"
              className="border-red-300 text-red-700 hover:bg-red-100"
              data-testid="facture-delete-btn"
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              {f.statut !== "en_suspens" || (f.encaissements || []).length > 0 ? "Forcer la suppression" : "Supprimer la facture"}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

// iter122 — Section d'affichage des lignes (read-only) + bouton d'édition si éligible
function FactureLignesSection({ facture, onEdit, userRole }) {
  const f = facture;
  const lignes = f.lignes || [];
  const encsActifs = (f.encaissements || []).filter((e) => e.statut !== "annule");
  const canEdit = (
    f.origine === "natif" &&
    f.statut !== "annule" &&
    !f.cloture_exercice_id &&
    encsActifs.length === 0 &&
    ["admin", "comptoir", "compta"].includes(userRole)
  );
  const reason = !canEdit ? (
    f.origine !== "natif" ? "Facture issue d'un OR ou Bexio — éditer via l'OR puis ré-émettre."
    : f.statut === "annule" ? "Facture annulée — voir 'Annuler l'annulation' pour réactiver."
    : f.cloture_exercice_id ? "Exercice clôturé (CO 957)."
    : encsActifs.length > 0 ? `${encsActifs.length} encaissement(s) — utilisez 'Annuler pour correction' pour ré-émettre.`
    : null
  ) : null;

  return (
    <Card className="p-5 border-gray-200 mt-4" data-testid="facture-lignes-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-heading font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-[#52a675]" /> Lignes ({lignes.length})
        </h3>
        {canEdit ? (
          <Button size="sm" variant="outline" onClick={onEdit} data-testid="facture-edit-lignes-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Modifier les lignes
          </Button>
        ) : reason && (
          <div className="text-[11px] text-gray-500 italic max-w-md text-right">{reason}</div>
        )}
      </div>
      {lignes.length === 0 ? (
        <div className="text-sm text-gray-400 py-4 text-center">Aucune ligne</div>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-1.5 text-left text-[10px] font-semibold text-gray-500 uppercase">Libellé</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">Qté</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">PU HT</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase">TVA</th>
                <th className="px-2 py-1.5 text-right text-[10px] font-semibold text-gray-500 uppercase whitespace-nowrap">Total HT</th>
              </tr>
            </thead>
            <tbody>
              {lignes.map((l, i) => (
                <tr key={i} className="border-t border-gray-100">
                  <td className="px-2 py-1.5">{l.libelle}</td>
                  <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{l.quantite}{l.unite ? ` ${l.unite}` : ""}</td>
                  <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{fmtChf(l.prix_unitaire_ht)}</td>
                  <td className="px-2 py-1.5 text-right text-xs text-gray-500">{l.tva_taux ?? l.tva_pct ?? 8.1}%</td>
                  <td className="px-2 py-1.5 text-right font-mono whitespace-nowrap">{fmtChf((l.quantite || 0) * (l.prix_unitaire_ht || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// iter122 — Dialog d'édition des lignes (utilise LignesEditor partagé)
function EditFactureLignesDialog({ open, onClose, facture, onSaved }) {
  const [lignes, setLignes] = useState([]);
  const [rabaisGlobal, setRabaisGlobal] = useState({ pct: 0, chf: 0 });
  const [titre, setTitre] = useState("");
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!open || !facture) return;
    setLignes((facture.lignes || []).map((l, i) => ({
      _localId: `edit-${i}-${Date.now()}`,
      libelle: l.libelle || "",
      quantite: l.quantite ?? 1,
      prix_unitaire_ht: l.prix_unitaire_ht ?? 0,
      tva_taux: l.tva_taux ?? l.tva_pct ?? 8.1,
      unite: l.unite || "",
      rabais_pct: l.rabais_pct || 0,
      rabais_chf: l.rabais_chf || 0,
      type_ligne: l.type_ligne || "libre",
      article_id: l.article_id || null,
      categorie_compta: l.categorie_compta || null,
    })));
    setRabaisGlobal({ pct: facture.rabais_global_pct || 0, chf: facture.rabais_global_chf || 0 });
    setTitre(facture.titre || "");
  }, [open, facture?.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const submit = async () => {
    const valides = lignes.filter((l) => l.libelle && l.libelle.trim());
    if (valides.length === 0) { toast.error("Au moins une ligne valide est requise"); return; }
    setSubmitting(true);
    try {
      const payload = {
        titre: titre || null,
        rabais_global_pct: parseFloat(rabaisGlobal.pct) || 0,
        rabais_global_chf: parseFloat(rabaisGlobal.chf) || 0,
        lignes: valides.map((l) => ({
          libelle: l.libelle,
          quantite: parseFloat(l.quantite) || 1,
          prix_unitaire_ht: parseFloat(l.prix_unitaire_ht) || 0,
          tva_taux: parseFloat(l.tva_taux) || 8.1,
          unite: l.unite || null,
          rabais_pct: parseFloat(l.rabais_pct) || 0,
          rabais_chf: parseFloat(l.rabais_chf) || 0,
          type_ligne: l.type_ligne || "libre",
          article_id: l.article_id || null,
          categorie_compta: l.categorie_compta || null,
        })),
      };
      const { data } = await api.patch(`/factures/${facture.id}/lignes`, payload);
      toast.success(`Facture ${facture.numero} mise à jour · ${data.ecritures_purgees} écriture(s) régénérée(s)`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  if (!facture) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="edit-facture-lignes-dialog">
        <DialogHeader>
          <DialogTitle>Modifier les lignes — Facture {facture.numero}</DialogTitle>
        </DialogHeader>
        <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 mb-3">
          ℹ️ Les écritures comptables (ventes 3200/3400 + TVA 2200) seront régénérées automatiquement.
          Cette action n&apos;est possible que tant qu&apos;aucun encaissement n&apos;a été enregistré.
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titre</Label>
            <Input value={titre} onChange={(e) => setTitre(e.target.value)} placeholder="Optionnel" />
          </div>
          <LignesEditor
            lignes={lignes}
            setLignes={setLignes}
            rabaisGlobal={rabaisGlobal}
            setRabaisGlobal={setRabaisGlobal}
            tvaDefaut={8.1}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={submitting} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="edit-facture-save">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EncaissementDialog({ nature, onClose, facture, comptes, onSaved }) {
  const open = !!nature;
  const cfg = NATURES[nature] || NATURES.encaissement;
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [montant, setMontant] = useState("");
  const [compteNum, setCompteNum] = useState("1020");
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (open && facture) {
      setDate(new Date().toISOString().slice(0, 10));
      // pré-remplit avec le reste à payer
      setMontant(facture.reste_a_payer > 0 ? facture.reste_a_payer.toFixed(2) : "");
    }
  }, [open, facture]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const m = parseFloat(montant) || 0;
  // iter300 — part de TVA contenue dans le montant, corrigée sur le 2200
  const tauxTva = facture?.montant_brut > 0 ? (facture.tva_810 || 0) / facture.montant_brut : 0;
  const partTva = nature === "encaissement" ? 0 : Math.round(m * tauxTva * 100) / 100;
  const compteCible = nature === "encaissement" ? compteNum : cfg.compte;

  const submit = async () => {
    if (!m || m <= 0) { toast.error("Montant invalide"); return; }
    setSubmitting(true);
    try {
      await api.post(`/factures/${facture.id}/encaissements`, {
        date,
        montant: m,
        nature,
        ...(nature === "encaissement" ? { compte_numero: compteNum } : {}),
      });
      toast.success(`${cfg.label} de ${m.toFixed(2)} CHF enregistré`);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {nature === "escompte" ? "Enregistrer un escompte accordé"
              : nature === "perte" ? "Enregistrer une perte sur client"
                : "Enregistrer un encaissement"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900">
            Facture <code className="font-mono font-semibold">{facture?.numero}</code> · Reste à payer : <span className="font-semibold">{facture?.reste_a_payer?.toFixed(2)} CHF</span>
          </div>
          {nature === "perte" && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-xs text-red-900" data-testid="enc-perte-avertissement">
              La créance est abandonnée : elle part en charge et la TVA déjà déclarée est récupérée.
              Cette opération reste tracée au journal.
            </div>
          )}
          <div>
            <Label>Date *</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="enc-date" />
          </div>
          <div>
            <Label>Montant (CHF) *</Label>
            <Input type="number" step="0.05" value={montant} onChange={(e) => setMontant(e.target.value)} data-testid="enc-montant" />
          </div>
          {nature === "encaissement" ? (
            <div>
              <Label>Compte d&apos;encaissement</Label>
              <CompteSelect
                value={compteNum}
                onValueChange={setCompteNum}
                options={comptes.map((c) => ({ value: c.numero, label: `${c.numero} – ${c.libelle}` }))}
                data-testid="enc-compte"
              />
            </div>
          ) : (
            <div className="text-xs text-gray-600" data-testid="enc-compte-fixe">
              Compte utilisé : <code className="font-mono font-semibold">{cfg.compte}</code>{" "}
              {nature === "escompte" ? "Escomptes" : "Pertes sur créances"}
            </div>
          )}
          <p className="text-[11px] text-gray-500">
            Écriture{partTva > 0 ? "s" : ""} générée{partTva > 0 ? "s" : ""} automatiquement :{" "}
            <code className="font-mono">{compteCible}</code> (D) {fmtChf(Math.max(0, m - partTva))} →{" "}
            <code className="font-mono">1100</code> Créances suisses (C)
            {partTva > 0 && (
              <>
                {" · "}<code className="font-mono">2200</code> TVA due (D) {fmtChf(partTva)} →{" "}
                <code className="font-mono">1100</code> (C)
              </>
            )}
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className={nature === "perte" ? "bg-red-600 hover:bg-red-700" : "bg-[#52a675] hover:bg-[#3d8a5c]"}
            data-testid="enc-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Bouton ✏️ pour éditer une écriture comptable directement depuis la fiche facture.
 * Réutilise l'endpoint PATCH /api/journal/ecritures/{eid} (iter132.h).
 * Refusé côté serveur si exercice clôturé ou facture payée/partielle.
 */
function EditEcritureFactureBtn({ ecriture, factureStatut, onSaved }) {
  const [open, setOpen] = useState(false);
  const [plan, setPlan] = useState([]);
  const [draft, setDraft] = useState({
    debit_compte_numero: ecriture.debit_compte_numero,
    credit_compte_numero: ecriture.credit_compte_numero,
    description: ecriture.description || "",
    reference: ecriture.reference || "",
  });
  const [saving, setSaving] = useState(false);

  const locked = factureStatut === "payee" || factureStatut === "partiel";

  useEffect(() => {
    if (!open || plan.length) return;
    api.get("/journal/plan-comptes").then((r) => setPlan((r.data || []).filter((c) => c.actif)));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (draft.debit_compte_numero === draft.credit_compte_numero) {
      toast.error("Débit et Crédit doivent être différents"); return;
    }
    setSaving(true);
    try {
      await api.patch(`/journal/ecritures/${ecriture.id}`, {
        debit_compte_numero: draft.debit_compte_numero,
        credit_compte_numero: draft.credit_compte_numero,
        description: draft.description,
        reference: draft.reference,
      });
      toast.success("Écriture modifiée");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Modification impossible");
    } finally { setSaving(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-gray-400 hover:text-blue-600 hover:bg-blue-50 p-1 rounded transition"
        title={locked ? "Facture payée/partielle : passez par une contre-écriture" : "Modifier l'écriture"}
        data-testid={`facture-ecriture-edit-${ecriture.id}`}
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Modifier l&apos;écriture comptable</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm">
              <div className="bg-gray-50 rounded p-2 text-xs text-gray-600">
                <div><b>Montant :</b> {ecriture.montant?.toFixed(2)} CHF · <b>Date :</b> {fmtDate(ecriture.date)}</div>
                <div className="text-gray-400 italic">Le montant et la date ne sont pas modifiables (utilisez une contre-écriture).</div>
              </div>
              {locked && (
                <div className="bg-red-50 border border-red-200 rounded px-2 py-1.5 text-xs text-red-700">
                  ⚠ Cette facture est <b>{factureStatut === "payee" ? "payée" : "partiellement payée"}</b> : l&apos;édition sera refusée par le serveur. Utilisez plutôt une contre-écriture.
                </div>
              )}
              <div>
                <Label className="text-xs">Référence</Label>
                <Input value={draft.reference} onChange={(e) => setDraft({ ...draft, reference: e.target.value })} data-testid="facture-ecriture-edit-reference" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Compte Débit</Label>
                  <select
                    value={draft.debit_compte_numero}
                    onChange={(e) => setDraft({ ...draft, debit_compte_numero: e.target.value })}
                    className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="facture-ecriture-edit-debit"
                  >
                    {plan.map((c) => <option key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Compte Crédit</Label>
                  <select
                    value={draft.credit_compte_numero}
                    onChange={(e) => setDraft({ ...draft, credit_compte_numero: e.target.value })}
                    className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm"
                    data-testid="facture-ecriture-edit-credit"
                  >
                    {plan.map((c) => <option key={c.numero} value={c.numero}>{c.numero} — {c.libelle}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} data-testid="facture-ecriture-edit-description" />
              </div>
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                Toute modification est tracée dans le journal d&apos;audit (CO art. 957).
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Annuler</Button>
              <Button onClick={save} disabled={saving || locked} data-testid="facture-ecriture-edit-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

